import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import api from "../lib/apiClient";

const COUNTRIES = ["All", "Palestine", "Jordan", "Turkey", "Saudi Arabia"];

export default function Brands() {
    const [brands, setBrands] = useState([]);
    const [counts, setCounts] = useState({});
    const [country, setCountry] = useState("All");

    useEffect(() => {
        api.get("/brands").then((r) => setBrands(r.data));
        api.get("/products").then((r) => {
            const c = {};
            r.data.forEach((p) => { c[p.brand] = (c[p.brand] || 0) + 1; });
            setCounts(c);
        });
    }, []);

    const shown = useMemo(
    () =>
        brands.filter(
            (b) =>
                b.name?.trim().toLowerCase() !== "ima products" &&
                (country === "All" || b.origin === country)
        ),
    [brands, country]
);

    const shortName = (name) => name.replace(" Snacks", "").replace(" Energy Drink", "").replace(" Iced Tea", "");

    return (
        <div data-testid="brands-page">
            <section className="bg-primary text-primary-foreground">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
                    <p className="text-sm uppercase tracking-[0.2em] text-secondary mb-3">Our Portfolio</p>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Our Featured Wholesale Brands</h1>
                    <p className="text-primary-foreground/70 mt-4 max-w-2xl">Browse trusted international food, beverage, snack, and grocery brands available for wholesale order requests.</p>
                </div>
            </section>

            {/* country filter */}
            <div className="border-b border-border bg-background">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-2">
                    {COUNTRIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => setCountry(c)}
                            data-testid={`country-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                            className={`text-sm px-5 py-2 border transition-colors ${country === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </div>

            <section className="py-16">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {shown.map((b) => (
                        <div key={b.id} className="border border-border bg-card hover:border-primary/60 transition-colors flex flex-col h-full" data-testid={`brand-card-${b.id}`}>
                            <div className="aspect-[4/3] bg-white overflow-hidden border-b border-border">
                                {b.logo ? (
                                    <img src={b.logo} alt={b.name} loading="lazy" className="w-full h-full object-contain p-4" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground grain">
                                        <span className="font-heading font-black text-2xl">{b.name}</span>
                                    </div>
                                )}
                            </div>
                            <div className="p-6 flex flex-col flex-1">
                                <h3 className="font-heading font-bold text-xl mb-1">{b.name}</h3>
                                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-secondary font-semibold mb-3">
                                    <MapPin size={13} /> {b.origin}
                                </div>
                                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{b.description}</p>
                                <Link to={`/catalog?brand=${encodeURIComponent(b.name)}`} className="mt-5 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3 font-semibold text-sm hover:bg-secondary hover:text-secondary-foreground transition-colors" data-testid={`view-brand-${b.id}`}>
                                    View {shortName(b.name)} Products <ArrowRight size={15} />
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="bg-muted py-16">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-heading font-bold text-3xl tracking-tight mb-4">Looking to distribute your brand?</h2>
                    <p className="text-muted-foreground mb-8 max-w-xl mx-auto">We partner with premium brands seeking reliable import, export and wholesale distribution.</p>
                    <Link to="/contact" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors">
                        Get in touch <ArrowRight size={18} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
